import { type AuthenticationStatus, SessionAdapter } from '../adapters';
import { oryKratosClient } from '../ory-kratos.client';
import type { ServiceResponse } from './types';

// AuthenticationStatus型を再エクスポート
export type { AuthenticationStatus } from '../adapters';
export const authService = {
  /**
   * 現在のセッションを取得する
   *
   * OryKratosClientのgetSessionメソッドを使用してセッションを取得します。
   * SessionAdapterを返します。
   *
   * @param aal - 認証保証レベル（"aal1" | "aal2"）。デフォルトは"aal1"。
   * @returns SessionAdapter。未認証の場合はdataがnull、エラーの場合はerrorが設定される
   *
   * @example
   * ```ts
   * const result = await authService.getSession();
   * if (result.data) {
   *   // SessionAdapterを直接使用
   *   console.log('Session ID:', result.data.id);
   *   console.log('Authentication Status:', result.data.authenticationStatus);
   *   console.log('AAL Level:', result.data.authenticatorAssuranceLevel);
   * }
   * ```
   */
  async getSession(aal: 'aal1' | 'aal2' = 'aal1'): Promise<ServiceResponse<SessionAdapter>> {
    return await oryKratosClient.getSession(aal);
  },
  /**
   * MFA認証情報を取得する
   *
   * whoami APIのauthentication_methodsからTOTP、WebAuthn、メールMFAの認証情報を取得します。
   *
   * @returns TOTP、WebAuthn、メールMFAの登録状態。エラー時は全てfalseを返す
   *
   * @example
   * ```ts
   * const credentials = await authService.getMfaCredentials();
   * if (credentials.hasTotp) {
   *   // TOTPが登録されている
   * }
   * if (credentials.hasEmailCode) {
   *   // メールMFAが登録されている
   * }
   * ```
   */
  async getMfaCredentials(): Promise<{
    hasTotp: boolean;
    hasEmailCode: boolean;
    allowedMethods: Array<'totp' | 'code'>;
    preferredMethod: 'totp' | 'code' | null;
  }> {
    try {
      // AAL1でセッション情報を取得（AAL2で取得すると、MFA未設定時に403になる可能性がある）
      const result = await authService.getSession('aal1');
      if (result.error || !result.data) {
        return {
          hasTotp: false,
          hasEmailCode: false,
          allowedMethods: [],
          preferredMethod: null
        };
      }

      // SessionValueのgetterを使用
      return {
        hasTotp: result.data.hasTotp,
        hasEmailCode: result.data.hasEmailCode,
        allowedMethods: result.data.allowedMethods,
        preferredMethod: result.data.preferredMethod
      };
    } catch (error) {
      console.error('Failed to get MFA credentials:', error);
      return {
        hasTotp: false,
        hasEmailCode: false,
        allowedMethods: [],
        preferredMethod: null
      };
    }
  },
  /**
   * 認証済みかどうかを判定する
   *
   * SessionAdapterのauthenticationStatusを使用して判定します。
   *
   * @returns 認証済み（authenticated）の場合はtrue、それ以外はfalse
   *
   * @example
   * ```ts
   * const isAuth = await authService.isAuthenticated();
   * if (isAuth) {
   *   // 認証済み（MFAが有効な場合はAAL2まで認証済み）
   * }
   * ```
   */
  async isAuthenticated(): Promise<boolean> {
    const status = await authService.getAuthenticationStatus();
    return status === 'authenticated';
  },
  /**
   * 認証状態を取得する
   *
   * whoami API（toSession）を呼び出して、以下の状態を判定します:
   * - unauthenticated: 401（未認証）→ サインインページに遷移
   * - authenticated: 200でmfa.enabled=false、または200でmfa.enabled=trueかつaal2 → そのままアクセス可能
   * - requires_mfa: 200でmfa.enabled=trueかつaal1 → 追加認証ページに遷移
   *
   * @returns 認証状態（AuthenticationStatus）
   *
   * @example
   * ```ts
   * const status = await authService.getAuthenticationStatus();
   * switch (status) {
   *   case "unauthenticated":
   *     // サインインページにリダイレクト
   *     break;
   *   case "requires_mfa":
   *     // 追加認証ページにリダイレクト
   *     break;
   *   case "authenticated":
   *     // そのままアクセス可能
   *     break;
   * }
   * ```
   */
  async getAuthenticationStatus(): Promise<AuthenticationStatus> {
    try {
      const result = await authService.getSession('aal1');

      // セッションが取得できない場合（401等）は未認証
      if (result.error || !result.data) {
        return 'unauthenticated';
      }

      // SessionAdapterのauthenticationStatusを使用
      return result.data.authenticationStatus;
    } catch (error) {
      console.error('Failed to get authentication status:', error);
      return 'unauthenticated';
    }
  }
};